import { PDFDocument, StandardFonts, rgb, type PDFImage } from 'pdf-lib'

// A4 in points.
const PAGE_WIDTH = 595.28
const PAGE_HEIGHT = 841.89
const MARGIN = 56
const BODY_SIZE = 11
const BODY_LEADING = 16
const SIGNATURE_BLOCK_HEIGHT = 170

const FOOTER_NOTE =
  'Documento generado en Zetyar Nexo. La firma es manuscrita digitalizada (firma electronica simple, Ley 19.799) y no constituye firma electronica avanzada.'

export interface SignedDocumentContent {
  readonly organizationName: string
  readonly logoUrl?: string | null
  readonly title: string
  readonly body: string
  readonly place: string
  readonly signerName: string
  readonly signerPosition?: string
  /** PNG or JPEG data URL. */
  readonly signatureDataUrl: string
  readonly signedAt: Date
}

/** Greedy wrap. The measure callback keeps this pure and testable without a font. */
export function wrapText(text: string, maxWidth: number, measure: (value: string) => number): string[] {
  const lines: string[] = []
  for (const paragraph of text.split('\n')) {
    const words = paragraph.trim().split(/\s+/).filter(Boolean)
    if (words.length === 0) {
      lines.push('')
      continue
    }

    let current = ''
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word
      if (measure(candidate) <= maxWidth || !current) {
        current = candidate
      } else {
        lines.push(current)
        current = word
      }
    }
    lines.push(current)
  }
  return lines
}

export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as unknown as ArrayBuffer)
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
}

// The logo lives in the public org-assets bucket. SVG cannot be embedded in a PDF, and a failure
// here must not block the document: the header falls back to the organization name alone.
async function embedLogo(pdf: PDFDocument, logoUrl: string): Promise<PDFImage | null> {
  try {
    const response = await fetch(logoUrl)
    if (!response.ok) return null
    const type = response.headers.get('content-type') ?? ''
    const bytes = new Uint8Array(await response.arrayBuffer())
    if (type.includes('png')) return await pdf.embedPng(bytes)
    if (type.includes('jpeg') || type.includes('jpg')) return await pdf.embedJpg(bytes)
    return null
  } catch (error) {
    console.warn('No se pudo incrustar el logo en el PDF:', error)
    return null
  }
}

async function embedSignature(pdf: PDFDocument, dataUrl: string): Promise<PDFImage> {
  const bytes = dataUrlToBytes(dataUrl)
  return dataUrl.startsWith('data:image/jpeg') ? await pdf.embedJpg(bytes) : await pdf.embedPng(bytes)
}

export async function buildSignedDocumentPdf(content: SignedDocumentContent): Promise<Uint8Array> {
  const pdf = await PDFDocument.create()
  const font = await pdf.embedFont(StandardFonts.Helvetica)
  const boldFont = await pdf.embedFont(StandardFonts.HelveticaBold)
  const contentWidth = PAGE_WIDTH - MARGIN * 2
  const muted = rgb(0.42, 0.45, 0.5)
  const ink = rgb(0.09, 0.13, 0.2)

  const logo = content.logoUrl ? await embedLogo(pdf, content.logoUrl) : null
  const signature = await embedSignature(pdf, content.signatureDataUrl)

  let page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT])
  let y = PAGE_HEIGHT - MARGIN

  const drawHeader = (target: typeof page) => {
    let headerBottom = PAGE_HEIGHT - MARGIN
    if (logo) {
      const scaled = logo.scaleToFit(130, 46)
      target.drawImage(logo, { x: MARGIN, y: headerBottom - scaled.height, width: scaled.width, height: scaled.height })
      headerBottom -= scaled.height + 10
    }
    target.drawText(content.organizationName, { x: MARGIN, y: headerBottom - 12, size: 12, font: boldFont, color: ink })
    headerBottom -= 24
    target.drawLine({
      start: { x: MARGIN, y: headerBottom },
      end: { x: PAGE_WIDTH - MARGIN, y: headerBottom },
      thickness: 0.8,
      color: rgb(0.85, 0.87, 0.9),
    })
    return headerBottom - 28
  }

  const drawFooter = (target: typeof page) => {
    const noteLines = wrapText(FOOTER_NOTE, contentWidth, (value) => font.widthOfTextAtSize(value, 7.5))
    let noteY = MARGIN - 14
    for (const line of noteLines.reverse()) {
      target.drawText(line, { x: MARGIN, y: noteY, size: 7.5, font, color: muted })
      noteY += 10
    }
  }

  y = drawHeader(page)

  page.drawText(content.title, { x: MARGIN, y, size: 16, font: boldFont, color: ink })
  y -= 26

  const dateLine = `${content.place.trim() ? `${content.place.trim()}, ` : ''}${formatLongDate(content.signedAt)}`
  page.drawText(dateLine, { x: MARGIN, y, size: 10, font, color: muted })
  y -= 26

  const measureBody = (value: string) => font.widthOfTextAtSize(value, BODY_SIZE)
  for (const line of wrapText(content.body, contentWidth, measureBody)) {
    if (y <= MARGIN + SIGNATURE_BLOCK_HEIGHT) {
      drawFooter(page)
      page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT])
      y = drawHeader(page)
    }
    if (line) {
      page.drawText(line, { x: MARGIN, y, size: BODY_SIZE, font, color: ink })
    }
    y -= BODY_LEADING
  }

  if (y <= MARGIN + SIGNATURE_BLOCK_HEIGHT) {
    drawFooter(page)
    page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT])
    y = drawHeader(page)
  }

  const signatureScaled = signature.scaleToFit(190, 70)
  const signatureY = Math.max(MARGIN + 70, y - 60)
  page.drawImage(signature, { x: MARGIN, y: signatureY, width: signatureScaled.width, height: signatureScaled.height })
  page.drawLine({
    start: { x: MARGIN, y: signatureY - 8 },
    end: { x: MARGIN + 210, y: signatureY - 8 },
    thickness: 0.8,
    color: rgb(0.7, 0.72, 0.76),
  })
  page.drawText(content.signerName, { x: MARGIN, y: signatureY - 24, size: 11, font: boldFont, color: ink })
  if (content.signerPosition) {
    page.drawText(content.signerPosition, { x: MARGIN, y: signatureY - 38, size: 9.5, font, color: muted })
  }
  page.drawText(content.organizationName, { x: MARGIN, y: signatureY - 52, size: 9.5, font, color: muted })

  drawFooter(page)

  return await pdf.save()
}
